import { useEffect } from "react";
import { useLicense } from "@coach/license-client";
import {
  markLicenseBlocked,
  markLicenseUnblocked,
  markServerConfirmedValid,
  useLicenseBlockFlag,
} from "./license-block-flag.js";
import { isBillingHardBlocked, useBillingState } from "./use-billing-state.js";

/**
 * Bridges the license-blocked state into the service worker so the lockdown
 * persists even when the browser is offline or the user clears their auth
 * cookies (Pitfall #17).
 *
 * - When the license is `blocked`, or billing reports a hard-blocked
 *   subscription (canceled / unpaid / paused-without-trial, never under a
 *   valid manual license — see `isBillingHardBlocked`), we post
 *   `LICENSE_BLOCKED` to the active SW. The SW then intercepts every
 *   subsequent navigation and serves a standalone blocked HTML page from
 *   the dedicated `coach-block-v1` cache — no app shell, no editor access.
 * - We post `LICENSE_UNBLOCKED` (clear the cache, let Workbox's default
 *   nav-fallback resume) only on server confirmation: the license state is
 *   `valid`/`grace` AND either a fresh `/billing/state` reports
 *   `effective.valid` or `/license/refresh` succeeded this session.
 *   The SW lets online navigations through so the app can lift a block
 *   (admin unlock); a cached token can still look valid locally for days
 *   after the server revoked it, so the local state alone must not clear
 *   the offline lockdown before `/license/refresh` re-blocks it. While the
 *   server is unreachable nothing is posted and the SW keeps its state.
 *
 * The same decision is mirrored into `license-block-flag.ts`, which keeps
 * the in-app gate closed on boots that are "online" but cannot reach the
 * server.
 *
 * Best-effort: SW absence (Tauri, no-SW dev) skips the postMessage.
 */
export function useSwLicenseBlockSync(): void {
  const { state } = useLicense();
  const billing = useBillingState();
  const blocked = state.kind === "blocked" || isBillingHardBlocked(billing.data);
  const { serverConfirmed } = useLicenseBlockFlag();
  // Server evidence: a fresh /billing/state with `effective.valid`, or a
  // successful /license/refresh this session (`serverConfirmed`).
  const confirmedValid =
    (state.kind === "valid" || state.kind === "grace") &&
    (billing.data?.effective?.valid === true || serverConfirmed);

  useEffect(() => {
    if (!blocked && !confirmedValid) return;
    // Mirror into the app's own persisted flag (also without a SW, e.g.
    // dev / Tauri) so the gate holds on "online but unreachable" boots.
    if (blocked) {
      markLicenseBlocked();
    } else {
      markServerConfirmedValid();
      markLicenseUnblocked();
    }

    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
      return;
    }
    const type = blocked ? "LICENSE_BLOCKED" : "LICENSE_UNBLOCKED";

    void (async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        const target = reg.active ?? reg.waiting ?? reg.installing;
        target?.postMessage({ type });
      } catch {
        /* SW not ready — next state change will retry */
      }
    })();
  }, [blocked, confirmedValid]);
}
